import ExportReportSettings from "@/services/api/exportReportSetting";
import { handleResponse } from "@/services/axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
   allExportReports: boolean;
   exportReportSetting: boolean;
   notificationUsers: boolean;
}

interface SaleOrderState {
    allExportReportList: any[];
    exportReportSettingList: any[];
    notificationUserList: any[];
    loading: LoadingState;
    error: string | null;
}

const initialState: SaleOrderState = {
    allExportReportList: [],
    exportReportSettingList: [],
    notificationUserList: [],
    loading: {
        allExportReports: false,
        exportReportSetting: false,
        notificationUsers: false,
    },
    error: null,
};

export const getAllExportReports = createAsyncThunk(
    "exportReport/getAllExportReports",
    async (_, { rejectWithValue }) => {
        try {
            const response = await ExportReportSettings.getAllExportReports();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch all export reports",
            );
        }
    },
);

export const getExportReportSetting = createAsyncThunk(
    "exportReport/getExportReportSetting",
    async (_, { rejectWithValue }) => {
        try {
            const response = await ExportReportSettings.getExportReportSetting();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch export report setting",
            );
        }
    },
);

export const addOrUpdateExportReportSetting = createAsyncThunk(
    "exportReport/addOrUpdateExportReportSetting",
    async (payload: any[], { rejectWithValue }) => {
        try {
            const response = await ExportReportSettings.addOrUpdateExportReportSetting(payload);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add or update export report setting",
            );
        }
    },
);

export const getNotificationUsers = createAsyncThunk(
    "exportReport/getNotificationUsers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await ExportReportSettings.getNotificationUsers();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch notification users",
            );
        }
    },
);

export const addOrUpdateNotificationUsers = createAsyncThunk(
    "exportReport/addOrUpdateNotificationUsers",
    async (payload: any[], { rejectWithValue }) => {
        try {
            const response = await ExportReportSettings.addOrUpdateNotificationUsers(payload);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add or update notification users",
            );
        }
    },
);

const exportReportSettingSlice = createSlice({
    name: "exportReport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllExportReports.pending, (state) => {
                state.loading.allExportReports = true;
            })
            .addCase(getAllExportReports.fulfilled, (state, action) => {
                state.loading.allExportReports = false;
                state.allExportReportList = action.payload || [];
            })
            .addCase(getAllExportReports.rejected, (state, action) => {
                state.loading.allExportReports = false;
                state.error = action.payload as string;
            })

            .addCase(getExportReportSetting.pending, (state) => {
                state.loading.exportReportSetting = true;
            })
            .addCase(getExportReportSetting.fulfilled, (state, action) => {
                state.loading.exportReportSetting = false;
                state.exportReportSettingList = action.payload || [];
            })
            .addCase(getExportReportSetting.rejected, (state, action) => {
                state.loading.exportReportSetting = false;
                state.error = action.payload as string;
            })

            .addCase(getNotificationUsers.pending, (state) => {
                state.loading.notificationUsers = true;
            })
            .addCase(getNotificationUsers.fulfilled, (state, action) => {
                state.loading.notificationUsers = false;
                state.notificationUserList = action.payload || [];
            })
            .addCase(getNotificationUsers.rejected, (state, action) => {
                state.loading.notificationUsers = false;
                state.error = action.payload as string;
            });
    },
});

export default exportReportSettingSlice.reducer;
